import { IQueryOptions, IExecutionOptions, IQueryContext } from '../models/query';
import { ConditionalHelper } from '../helpers/ConditionalHelper';
import { IEntity } from '../models/api/search';
import { Query } from './Query';
import { TranslationHelper } from '../helpers/TranslationHelper';
import { IQueryOptionsEntityBase } from '../models/query/options/IQueryOptionsEntityBase';
import { BaseQuery } from './BaseQuery';
import { isNullOrUndefined } from '../utils/typeChecks';

export const MAX_CALL_STACK_LEVEL: number = 50;

export abstract class BaseEntityQuery<TChildOptions extends IQueryOptionsEntityBase> extends BaseQuery<TChildOptions> {
	constructor(queryOptions: IQueryOptions, translationHelper?: TranslationHelper) {
		super(queryOptions, translationHelper);
	}
	getSubCollection(executionOptions: IExecutionOptions): any[] {
		const { queryOptionsType } = this.queryOptions;
		return this.processEntities(executionOptions, (entity) => {
			if (entity && entity[queryOptionsType]) {
				return entity[queryOptionsType];
			}
		});
	}
	processEntities(executionOptions: IExecutionOptions, processingFunction: (entity: IEntity) => any | any[] | undefined): any | any[] | undefined {
		const conditionalHelper = new ConditionalHelper({ conditions: [] }, this.translationHelper);
		const context = Query.getContext(executionOptions, this.queryOptions);
		if (context) {
			const { entityByType, documentEntityByKeys } = context;
			const {
				entityType,
				entityConditional,
				documentEntityKey,
				entityKey,
				entityIndex,
			} = this.options;

			let entities: IEntity[] = [];
			if (entityKey) {
				entities = this.getLinkedEntities(entityKey, context, entityIndex);
				if (entities && entityConditional) {
					entities = entities.filter(entity => conditionalHelper.matches(entity, entityConditional, executionOptions));
				}
			}
			else {
				const documentEntity = documentEntityByKeys && documentEntityKey && documentEntityByKeys[documentEntityKey];
				const entity = documentEntity || (entityByType && entityType && entityByType[entityType]);
				if (entity && (!entityConditional || conditionalHelper.matches(entity, entityConditional, executionOptions))) {
					entities = [entity as IEntity];
				}
			}
			const processingResults = [];
			for (const entity of entities) {
				processingResults.push(processingFunction(entity));
			}
			return processingResults.length === 0 ? undefined :
				processingResults.length === 1 ? processingResults[0] :
					processingResults;
		}
		return;
	}

	getEntities(executionOptions: IExecutionOptions, context: IQueryContext): IEntity[] | undefined {
		const conditionalHelper = new ConditionalHelper({ conditions: [] }, this.translationHelper);
		if (context) {
			const { entities } = context;
			const {
				queryId,
			} = this.queryOptions;
			const {
				entityConditional,
			} = this.options;
			const entitiesContext = (entities && queryId && entities[queryId]);
			if (entitiesContext) {
				return entitiesContext.values.filter(entity => (!entityConditional || conditionalHelper.matches(entity, entityConditional, executionOptions))) as IEntity[];
			}
		}
		return;
	}

	getLinkedEntities(entityKey: string, context: IQueryContext, entityIndex: number): IEntity[] {
		const { entityMap, entitiesById } = context;
		if (entityMap && entityKey && entityMap[entityKey]) {
			const linkedEntitiesIds = entityMap[entityKey];
			const linkedEntities = [];
			if (!isNullOrUndefined(entityIndex)) {
				const linkedEntityId = Array.from(linkedEntitiesIds)[entityIndex];
				if (linkedEntityId) {
					linkedEntities.push(entitiesById.get(linkedEntityId));
					return linkedEntities;
				}
				console.warn(`getLinkedEntities: No entity id found at childIndex of ${entityIndex} for entityKey "${entityKey}". Using all linked entities`);
			}
			linkedEntitiesIds.forEach((linkedEntityId) => {
				linkedEntities.push(entitiesById.get(linkedEntityId));
			});
			return linkedEntities;
		}
		return [];
	}
}